import styled from "styled-components";
import { getTheme, Tooltip, Typography,} from "@bracket/ui";
import { getTokenDefinition, } from "@bracket/chainography";
import type { FunctionComponent, ReactNode, } from "react";
import type { TokenIdentifier, } from "@bracket/types";


export declare type MetricCardProps = {
  title: string;
  value: string | ReactNode;
  tooltipMessage: string;
  actionButton?: React.ReactNode;
	valuetype?: "COMPONENT" | "WEI";
	precision?: number;
	token: TokenIdentifier;
};




/**
 * Renders a metric card component that displays a title, value, and optional action button.
 * The value can be displayed as either a component or a WEI value that gets converted to ETH.
 * 
 * @param {MetricCardProps} props - The component props
 * @param {string} props.title - The title text displayed at the top of the card
 * @param {string | ReactNode} props.value - The main value to display, can be a string or React component
 * @param {string} props.tooltipMessage - The message shown in the tooltip when hovering over the info icon
 * @param {ReactNode} [props.actionButton] - Optional action button to display next to the value
 * @param {"COMPONENT" | "WEI"} [props.valuetype="COMPONENT"] - Determines how the value is displayed
 * @param {number} [props.precision=5] - Number of decimal places to show for WEI values
 * @returns {JSX.Element} A styled card component displaying the metric information
 */
export const MetricCard: FunctionComponent<MetricCardProps> = ({
	title,
	value,
	tooltipMessage,
	actionButton,
	valuetype = "COMPONENT",
	precision = 5,
	token,
}) => {

	const tok = getTokenDefinition(token);

	return (
		<Card>
			<CardTitleSection>
				<Typography variant="paragraphSM" color="var(--text-subheading)">
					{title}
				</Typography>
				<Tooltip message={tooltipMessage} />
			</CardTitleSection>

			<CardBodySection>
				<Typography variant="textMD" color="var(--text-heading-title)">
				{
					valuetype === "WEI"
						? (Number(value as string) / (10**tok.decimals)).toFixed(tok.symbol === "USDC" ? 2 : precision)
						: value
				}
				</Typography>
				{actionButton}
			</CardBodySection>
		</Card>
	);

};


const Card = styled.div`
	display: flex;
	gap: 8px;
	padding: 24px;
	border-radius: 8px;
	// border: 1px solid ${() => getTheme().outline};
	background-color: ${() =>getTheme().surface};
	box-shadow: ${() => getTheme().shadows.primary};
	flex-direction: column;

	@media (min-width: 768px) {
		gap: 12px;
	}
`;

const CardTitleSection = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
`;

const CardBodySection = styled.div`
	display: flex;
	flex-direction: column;

	@media (min-width: 768px) {
		flex-direction: row;
		align-items: center;
		gap: 2rem;
	}
`;
